import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subscription } from 'rxjs';
import { APIService } from '../../fleet-management/backend-service/backend-service';
import { NotificationService } from '../../shared/services/notification.service';
import { ConfirmationDialogService } from '../../shared/services/confirmation-dialog.service';
import { LoadingService } from '../../services/loading.service';

export interface Permission {
  key: string;
  label: string;
  view: boolean;
  edit: boolean;
  children?: Permission[];
}

export interface RolePermission {
  id: number;
  roleCode: string;
  permissionId: number;
  canView: boolean;
  canEdit: boolean;
  permission: { id: number; module: string };
}

export interface RoleApiItem {
  id: number;
  roleCode: string;
  name: string;
  description: string;
  isActive: boolean;
  rolePermissions: RolePermission[];
}

export interface RoleItem {
  id: number;
  roleCode: string;
  name: string;
  permissions: Permission[];
}

const DEFAULT_PERMISSIONS: Permission[] = [
  { key: 'dashboard', label: 'Dashboard', view: false, edit: false },
  { key: 'user_management', label: 'User Management', view: false, edit: false },
  { key: 'customer_management', label: 'Customer Management', view: false, edit: false },
  { key: 'consignee_registration', label: 'Consignee Registration', view: false, edit: false },
  { key: 'shipping_order', label: 'Shipping Order', view: false, edit: false },
  { key: 'booking_list', label: 'Booking List', view: false, edit: false },
  {
    key: 'fleet_management', label: 'Fleet Management', view: false, edit: false,
    children: [
      { key: 'truck_profile', label: 'Truck Profile', view: false, edit: false },
      { key: 'driver_profile', label: 'Driver Profile', view: false, edit: false },
      { key: 'transport_management', label: 'Transport Management', view: false, edit: false },
    ]
  },
  { key: 'report', label: 'Report', view: false, edit: false },
  { key: 'accounting', label: 'Accounting', view: false, edit: false },
  { key: 'settings', label: 'Settings', view: false, edit: false },
];

function deepClonePermissions(perms: Permission[]): Permission[] {
  return perms.map(p => ({
    ...p,
    children: p.children ? deepClonePermissions(p.children) : undefined
  }));
}

@Component({
  selector: 'app-user-settings',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './user-settings.component.html',
  styleUrl: './user-settings.component.scss'
})
export class UserSettingsComponent implements OnInit, OnDestroy {
  roleList: RoleItem[] = [];
  selectedRole: RoleItem | null = null;
  newRoleName: string = '';
  isLoading: boolean = false;
  isSaving: boolean = false;
  private loadingSubscription: Subscription;

  constructor(
    private apiService: APIService,
    private notification: NotificationService,
    private confirmationDialog: ConfirmationDialogService,
    public loadingService: LoadingService
  ) {
    this.loadingSubscription = this.loadingService.isLoading$.subscribe(loading => this.isLoading = loading);
  }

  ngOnInit(): void {
    this.getRolesFromAPI();
  }

  ngOnDestroy() {
    if (this.loadingSubscription) {
      this.loadingSubscription.unsubscribe();
    }
  }

  getRolesFromAPI(): void {
    const loginData = JSON.parse(localStorage.getItem('loginData') || '{}');
    this.isLoading = true;
    this.apiService.get('/api/roles', loginData.token).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        console.log('Roles API response:', response);
        if (response?.body?.data) {
          this.roleList = (response.body.data as RoleApiItem[]).map(role => ({
            id: role.id,
            roleCode: role.roleCode,
            name: role.name,
            permissions: this.mapApiPermissions(role.rolePermissions)
          } as RoleItem));
          if (this.roleList.length > 0 && !this.selectedRole) {
            this.selectedRole = this.roleList[0];
          }
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Error loading roles:', err);
      }
    });
  }

  private moduleToKey(module: string): string {
    return module.replace(/([A-Z])/g, '_$1').toLowerCase();
  }

  private mapApiPermissions(rolePermissions: RolePermission[]): Permission[] {
    const perms = deepClonePermissions(DEFAULT_PERMISSIONS);
    for (const rp of rolePermissions) {
      const key = this.moduleToKey(rp.permission.module);
      this.applyPermission(perms, key, rp.canView, rp.canEdit);
    }
    return perms;
  }

  private applyPermission(perms: Permission[], key: string, canView: boolean, canEdit: boolean): boolean {
    for (const p of perms) {
      if (p.key === key) {
        p.view = canView;
        p.edit = canEdit;
        return true;
      }
      if (p.children && this.applyPermission(p.children, key, canView, canEdit)) {
        this.syncParentFromChildren(p);
        return true;
      }
    }
    return false;
  }

  selectRole(role: RoleItem): void {
    this.selectedRole = role;
  }

  addRole(): void {
    const name = this.newRoleName.trim();
    if (!name || this.roleList.some(r => r.name === name)) return;

    const roleCode = name.toUpperCase().replace(/\s+/g, '_');
    const loginData = JSON.parse(localStorage.getItem('loginData') || '{}');
    const payload = { roleCode, name, description: '', isActive: true };

    this.apiService.post('/api/roles', payload, loginData.token).subscribe({
      next: (response) => {
        if (response.status === 200 || response.status === 201) {
          this.newRoleName = '';
          this.getRolesFromAPI();
          this.notification.success('Role added successfully!');
        } else {
          this.notification.error('Failed to add role.');
        }
      },
      error: (err) => {
        console.error('Error adding role:', err);
        this.notification.error('Failed to add role.');
      }
    });
  }

  removeRole(role: RoleItem): void {
    this.confirmationDialog.confirmDelete(role.name).subscribe(confirmed => {
      if (!confirmed) return;
      const loginData = JSON.parse(localStorage.getItem('loginData') || '{}');
      this.apiService.delete('/api/predefined-data/USER_ROLES/' + role.id, loginData.token).subscribe({
        next: (response: any) => {
          if (response.status === 200) {
            if (this.selectedRole?.id === role.id) this.selectedRole = null;
            // this.getSettingsFromAPI();
            this.notification.success('Role deleted successfully');
          }
        },
        error: (err) => console.error('Error deleting role:', err)
      });
    });
  }

  mergePermissions(defaults: Permission[], saved: any[]): Permission[] {
    return defaults.map(p => {
      const match = saved.find((s: any) => s.key === p.key);
      if (match) {
        return {
          ...p,
          view: !!match.view,
          edit: !!match.edit,
          children: p.children ? this.mergePermissions(p.children, match.children || []) : undefined
        };
      }
      return p;
    });
  }

  onViewChange(perm: Permission): void {
    if (!perm.view) {
      perm.edit = false;
    }
    if (perm.children) {
      perm.children.forEach(child => {
        child.view = perm.view;
        if (!perm.view) child.edit = false;
      });
    }
  }

  onEditChange(perm: Permission): void {
    if (perm.edit) {
      perm.view = true;
    }
    if (perm.children) {
      perm.children.forEach(child => {
        child.edit = perm.edit;
        if (perm.edit) child.view = true;
      });
    }
  }

  onChildViewChange(parent: Permission, child: Permission): void {
    if (!child.view) {
      child.edit = false;
    }
    this.syncParentFromChildren(parent);
  }

  onChildEditChange(parent: Permission, child: Permission): void {
    if (child.edit) {
      child.view = true;
    }
    this.syncParentFromChildren(parent);
  }

  syncParentFromChildren(parent: Permission): void {
    if (!parent.children) return;
    parent.view = parent.children.some(c => c.view);
    parent.edit = parent.children.some(c => c.edit);
  }

  savePermissions(): void {
    if (!this.selectedRole) return;
    const loginData = JSON.parse(localStorage.getItem('loginData') || '{}');
    if (!loginData.token) return;

    this.isSaving = true;
    const payload = { modules: this.buildModulesPayload(this.selectedRole.permissions) };

    this.apiService.put(`/api/roles/${this.selectedRole.roleCode}/permissions`, payload, loginData.token).subscribe({
      next: (response) => {
        this.isSaving = false;
        if (response.status === 200 || response.status === 201) {
          this.notification.success('Permissions saved successfully!');
        } else {
          this.notification.error('Failed to save permissions.');
        }
      },
      error: (err) => {
        this.isSaving = false;
        console.error('Error saving permissions:', err);
        this.notification.error('Failed to save permissions.');
      }
    });
  }

  private keyToModule(key: string): string {
    return key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  }

  private buildModulesPayload(perms: Permission[]): Record<string, { view: boolean; edit: boolean }> {
    const modules: Record<string, { view: boolean; edit: boolean }> = {};
    for (const p of perms) {
      modules[this.keyToModule(p.key)] = { view: p.view, edit: p.edit };
      if (p.children) {
        for (const child of p.children) {
          modules[this.keyToModule(child.key)] = { view: child.view, edit: child.edit };
        }
      }
    }
    return modules;
  }

  getRoleInitial(role: RoleItem): string {
    return (role.name || '?').charAt(0).toUpperCase();
  }
}
